/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './ScrollNav.scss';
import { Paper, Separator, Icon, Size } from '@vaisala-rockhopper/components';
import { Container } from 'react-grid-system';
import paramConfig from '../../../modules/calculator/parameterConfig_NEW.json';
import PhaseIndicator from './PhaseIndicator';
import { ScrollNavContext } from '../../context/ScrollNavContext';
import { useTranslation } from 'react-i18next';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import { isMobileOnly } from 'react-device-detect';
import Analytics from '../../../modules/analytics/Analytics';

const isInViewport = (phaseEl: Element, index: number, containerEl: any, forcedRotation: boolean) => {
  if (!phaseEl) return false;
  const container = document.getElementById(containerEl?.target?.id)?.getBoundingClientRect();
  let top = phaseEl.getBoundingClientRect().top;
  let bottom = phaseEl.getBoundingClientRect().bottom;
  if (container && top && bottom) {
    if (forcedRotation) {
      top = phaseEl.getBoundingClientRect().right;
      bottom = phaseEl.getBoundingClientRect().left;
      //console.log('index: ' + index + ', ' + 'height:' + container.width + ', ' + 'top: ' + top + ', bottom: ' + bottom);
      const isIt = top < container.width && bottom > 0;
      return isIt;
    } else {
      //console.log('index: ' + index + ', ' + 'height:' + container.height + ', ' + 'top: ' + top + ', bottom: ' + bottom);
      const isIt = top >= 0 || bottom >= container.height;
      return isIt;
    }
  }
};

export const handleScroll = (e: any, phaseSetter: (phase: number) => void, selector?: string | null, graphActive?: boolean) => {
  let phases = document.querySelectorAll('.calculator-card.calculator-card--default');
  let rotation = false;
  if (selector) {
    phases = document.querySelectorAll(`.${selector}`);
  }
  if (window.innerHeight > window.innerWidth && graphActive) {
    rotation = true;
  }
  if (phases?.length > 0) {
    for (let i = 0; i < phases.length; i++) {
      const phase = phases[i];
      if (isInViewport(phase, i, e, rotation)) {
        phaseSetter(i + 1);
        break;
      } else {
        continue;
      }
    }
  }
};

export const scrollToPhase = (phase: number, phaseSetter: (phase: number) => void, isGraphAndMobile?: boolean) => {
  let phaseEl = document.getElementById(`phase-${phase}`);
  let container = document.getElementById('mobile-calculator') || document.getElementById('desktop-calculator');
  let rotation = false;
  if (isGraphAndMobile) {
    if (window.innerHeight > window.innerWidth) {
      rotation = true;
    }
    phaseEl = document.getElementById(`graph-phase-${phase}`);
    container = document.getElementById('graph-card-container');
  }
  if (phaseEl && container) {
    let offset = 0;
    if (isMobileOnly && !rotation) {
      offset = 64;
    } else {
      offset = 0;
    }
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 1) {
      phaseSetter(phase);
    }
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function () {
        phaseSetter(phase);
        container?.removeEventListener('scroll', handleScroll);
      }, 100);
    };
    container.addEventListener('scroll', handleScroll);
    container.scroll({ top: phaseEl.offsetTop - offset, behavior: 'smooth' });
  }
};

interface ScrollNavProps {
  activeMethod: number;
  setGraphActive: (value: boolean) => void;
}

const ScrollNav = React.forwardRef(({ activeMethod, setGraphActive }: ScrollNavProps, ref: any) => {
  const { t } = useTranslation();
  const [activePhase, setActivePhase] = React.useContext(ScrollNavContext);
  const method = paramConfig?.methods?.find((x) => x.id === activeMethod);

  React.useEffect(() => {
    document.body.classList.add('scroll-nav-active');
    return () => document.body.classList.remove('scroll-nav-active');
  }, []);

  return (
    <div className={activeMethod > -1 ? 'scroll-nav scroll-nav--active' : 'scroll-nav'}>
      <Container className="scroll-nav__container">
        <div className="scroll-nav__column">
          <Separator />
          <Paper className="scroll-nav__wrap">
            <div>
              {method?.cards &&
                method?.cards?.length > 1 &&
                method?.cards?.map((card: any, i: any) => {
                  return (
                    <button
                      key={i}
                      className="scroll-nav__button"
                      onClick={() => {
                        scrollToPhase(i + 1, setActivePhase);
                      }}
                    >
                      <PhaseIndicator phase={i + 1} active={i + 1 === activePhase} />
                    </button>
                  );
                })}
            </div>
            <button
              className="scroll-nav__graph-button"
              onClick={() => {
                Analytics.log('navigation.graph_open');
                setGraphActive(true);
              }}
            >
              <Icon size={Size.XL} className="icon" name={VaiIcon.Graph} />
              <span className="text">{t('ui.graph')}</span>
            </button>
          </Paper>
        </div>
      </Container>
    </div>
  );
});

export default ScrollNav;
